/**
 * /reset-password?token=XXX — choose a new password.
 *
 * Reads the token from the URL, validates the password client-side
 * (length + match), submits to POST /api/auth/reset-password. On success
 * redirects to /login with a toast. The server invalidates all sessions
 * so the user has to sign in fresh.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LENGTH = 10;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Client-side validation just for instant feedback — server is the gate.
  const lengthOk = password.length >= MIN_LENGTH;
  const lengthCapped = password.length <= 128;
  const match = password === confirm;
  const canSubmit = !!token && lengthOk && lengthCapped && match && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSubmitted(true);
      toast.success('Password updated. Sign in with your new password.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || 'Could not reset your password.')
        : 'Could not reset your password.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="reset-password-missing-token">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <AlertTriangle className="w-12 h-12 text-[#DC2626] mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold text-[#020617] mb-2">No token in this link.</h1>
            <p className="text-[#64748B] mb-6">
              The reset link is incomplete. Request a new one from the forgot-password page.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#0052FF] text-white hover:bg-[#003ECC]">Request a new link</Button>
            </Link>
          </div>
        </div>
        <Footer variant="full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="reset-password-page">
      <div className="flex-1 flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>

          {submitted ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm text-center" data-testid="reset-password-success">
              <CheckCircle2 className="w-12 h-12 text-[#047857] mx-auto mb-4" />
              <h1 className="text-2xl font-extrabold text-[#020617] mb-2">Password updated.</h1>
              <p className="text-[#64748B]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-4">
                <KeyRound className="w-6 h-6 text-[#0052FF]" />
              </div>
              <h1 className="text-4xl tracking-tighter font-extrabold text-[#020617] mb-2" data-testid="reset-password-title">
                Choose a new password
              </h1>
              <p className="text-base text-[#64748B] mb-8">
                At least {MIN_LENGTH} characters. We&apos;ll check it against known breach lists — no other rules.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-[#020617]">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={MIN_LENGTH}
                    maxLength={128}
                    className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                    placeholder="At least 10 characters"
                    data-testid="reset-password-input"
                  />
                  <PasswordStrengthMeter password={password} minLength={MIN_LENGTH} />
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-sm font-medium text-[#020617]">Confirm new password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                    placeholder="Type it again"
                    data-testid="reset-password-confirm-input"
                  />
                  {confirm && !match && (
                    <p className="text-xs text-[#DC2626] mt-1.5" data-testid="reset-password-mismatch">
                      Passwords don&apos;t match.
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC] transition-colors disabled:opacity-60"
                  data-testid="reset-password-submit-button"
                >
                  {submitting ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0052FF] to-[#003ECC] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-bold mb-4">Length beats complexity.</h2>
          <p className="text-lg opacity-90">
            A 16-character passphrase like &quot;correct horse battery staple&quot; is stronger than &quot;P@ssw0rd!&quot;.
            We don&apos;t force symbols or numbers — we just check your choice isn&apos;t in a known breach list.
          </p>
        </div>
      </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default ResetPassword;
